// Central content for Jessica Dannielle Johnson's portfolio

export const PROFILE = {
  name: "Jessica Dannielle Johnson",
  first: "Jessica",
  roles: ["Senior Content Developer", "Instructional Designer", "ERP Trainer"],
  location: "Orland Park, IL, working nationwide",
  email: "weatherthastorm@gmail.com",
  phone: "708.252.1016",
  phoneHref: "tel:+17082521016",
  linkedin: "https://www.linkedin.com/in/jessicadannielle/",
  resume: "/assets/resume.pdf",
  headshot: "/assets/jessica-headshot.jpg",
};

export const CREATIVE_ROLES = [
  "Expert Graphic Designer",
  "Photographer",
  "Videographer",
  "Image Editor",
  "Photo Manipulation Artist",
  "Visual Content Editor",
  "Communications",
  "Interior Designer",
  "Licensed REALTOR\u00AE",
];

export const TAGLINE =
  "I'm not a jack of all trades looking for somewhere to fit. I'm a specialist with range who knows exactly where to focus it.";

export const SUMMARY =
  "I'm a Learning Experience Designer who has run both full ERP implementations and system upgrades, from small-scale rollouts to large enterprise deployments. I'm equally at home on fast, deadline-driven implementations and longer, more deliberate content builds.";

export const SUMMARY_DETAIL = [
  "New platforms aren't a problem. Years across systems have taught me that ERP platforms and instructional design tools have more in common than not: similar workflows, similar user friction points, just a different interface. That pattern recognition lets me ramp on an unfamiliar system fast, instead of needing months of runway before I can start building. I also bring hands-on experience with AI-enabled tools like Microsoft Copilot and WellSaid Labs when a project calls for them, along with a clear understanding of the regulatory and compliance boundaries around AI use.",
  "My functional depth spans Finance, Supply Chain, and HCM, with training built across a wide range of processes inside each. Knowing the theory makes you an instructional designer; knowing the tools lets you build courses; knowing the complete project workflow is what lets you deliver learning that actually works, and my multi-level project experience is where that comes from. I also design for multimodality, because a single rollout has to train very different end users, from the shop floor and the field to the front office.",
];

export const STATS = [
  { value: "13", label: "Years in Instructional Design" },
  { value: "20", label: "Years in graphic design" },
  { value: "25+", label: "Enterprise ERP rollouts" },
  { value: "7", label: "Industries served" },
];

export const INDUSTRIES = [
  "Healthcare", "Utilities", "Government", "Higher Education", "Retail", "Corporate / Enterprise", "Music Industry",
];

export const MANIFESTO = [
  {
    no: "01",
    title: "Discover",
    body: "Every engagement starts with a needs analysis. I sit with SMEs and stakeholders to map the real gap between where learners are and where the business needs them to be, grounded in adult learning theory rather than assumptions.",
  },
  {
    no: "02",
    title: "Design",
    body: "ADDIE meets agile. I blueprint the solution (objectives, structure, assessment strategy), then move fast in Jira-driven sprints, iterating with reviewers so the design earns buy-in before a single screen is built.",
  },
  {
    no: "03",
    title: "Develop",
    body: "Storyline, Rise, Captivate, Camtasia, Vyond, Word, PowerPoint, Inkling, and Workday Learning are my instruments (with Oracle UPK in its day, now a retired system). I build simulations, e-learning, job aids, and AI-narrated video for platforms like Oracle, PeopleSoft, Infor, and Workday, all SCORM-compliant and LMS-ready, produced under aggressive timelines without cutting corners.",
  },
  {
    no: "04",
    title: "Deliver",
    body: "From Train-the-Trainer bootcamps to ILT for thousands, webinars, assessments, and go-live hypercare, I don't just hand off content. I gather feedback, measure whether learners can actually perform the task, and make sure the change sticks.",
  },
];

export const COURSE_PROCESS = [
  "I start with the training requirement in hand and get clear on what success looks like",
  "I run a kickoff with the stakeholders and SMEs who actually own the content",
  "I dig into the business goals, audience, scope, timelines, and whatever content already exists",
  "I analyze all of it and call out the real gaps early, before they cost us later",
  "I draft the course outline and shape the learning strategy around how people actually learn",
  "I build a storyboard or prototype so everyone can see it before I commit hours to production",
  "I gather the visuals and learning assets that make it feel intentional, not templated",
  "I get stakeholder sign-off on the design first, so approvals don't derail development",
  "I develop the course in the authoring tool that fits the goal, not just my favorite one",
  "I run my own QA and testing before anyone else has to catch it",
  "I fold in review feedback without losing the through-line of the design",
  "I get final approval, publish to the LMS, and deploy, then make sure it actually landed",
];

export const AI_PRACTICE = {
  heading: "AI, used with intent.",
  body: "I work at an expert level with Microsoft Copilot, ChatGPT, and Claude across the entire content lifecycle, from research and drafting to iteration and quality checks. I've built Rise courses that teach internal teams to use these tools well, and on the side I run community education sessions that help everyday people get fluent and confident with AI.",
  chips: ["Microsoft Copilot", "ChatGPT", "Claude", "Gemini"],
  badge: "Community AI Educator",
};

export const TOOLS = [
  "Articulate Storyline", "Articulate Rise", "Adobe Captivate", "Oracle UPK",
  "Camtasia", "Vyond / Powtoon", "Workday Learning", "WellSaid AI Voiceover",
  "SCORM Compliance", "LMS Administration", "Inkling Habitat", "Jira / Agile",
  "Microsoft Copilot", "Claude / ChatGPT", "Adobe Photoshop / InDesign", "Canva",
];

export const ERP_SYSTEMS = [
  "Oracle Cloud", "SAP", "PeopleSoft", "Infor CloudSuite", "Workday", "Oracle Fusion",
  "Oracle C2M / CC&B", "WAM / MWM", "Hyperion", "Custom In-House Systems", "Proprietary Client Platforms",
];

export const MODULE_PILLARS = ["Finance", "Supply Chain Management (SCM)", "Human Capital Management (HCM)"];

export const CREATIVE_INTRO =
  "My experience in photography and videography spans more than two decades. In the early 2000s, I worked as an onboard photographer for the Spirit of Chicago tour boat for four years, capturing guests, events and experiences along the city's waterfront. From there, I became known as the \"friend with the camera,\" documenting everything from personal milestones and live events to creative projects and professional productions. Today, I provide professional photography, videography and cinematic storytelling for brands, organizations, artists and community events on the side. My work with Timbaland is a major chapter in that journey, but it is far from where my story behind the camera began.";

// Instructional Design artifacts, real work samples
export const ID_SAMPLES = [
  {
    key: "storyline",
    type: "live",
    kind: "Interactive e-Learning",
    title: "UC Merced, Delegation",
    meta: "Articulate Storyline · 13 slides · ~4 min",
    desc: "A published, fully interactive Storyline course. Launch it right here in the browser.",
    src: "/assets/storyline-uc-merced/story.html",
  },
  {
    key: "storyline-brooks",
    type: "live",
    kind: "Interactive e-Learning",
    title: "Brooks Cloud Navigation Overview",
    meta: "Articulate Storyline · 13 slides · ~5 min",
    desc: "Another published, fully interactive Storyline course. Launch it right here in the browser.",
    src: "/assets/storyline-brooks/story.html",
  },
  {
    key: "qrg",
    type: "image",
    kind: "Job Aid / QRG",
    title: "Reconnect Meter Flip Card",
    meta: "Laminated quick-reference guide · Utility field training",
    desc: "Step-by-step quick-reference card built for utility field workers on an Oracle C2M implementation.",
    src: "/assets/qrg-card.jpg",
  },
  {
    key: "newbalance",
    type: "doc",
    kind: "PDF · Master Template",
    title: "New Balance Canva Master Template",
    meta: "Reusable design system · Retail",
    desc: "A Canva master template built for wide, self-serve use across a retail client's training teams.",
    src: "/assets/newbalance-template.pdf",
    thumb: "/assets/thumbs/newbalance.jpg",
  },
  {
    key: "press",
    type: "doc",
    kind: "PDF · Communications",
    title: "CreatorVerse x Timbaland Press Release",
    meta: "PDF document",
    desc: "Launch communications piece written and formatted for a creator-technology initiative.",
    src: "/assets/creatorverse-press-release.pdf",
    thumb: "/assets/thumbs/press.jpg",
  },
  {
    key: "supplier",
    type: "doc",
    kind: "PPTX · ILT Deck",
    title: "Supplier Management Overview",
    meta: "PowerPoint deck",
    desc: "Instructor-led training deck covering an ERP supplier management module.",
    src: "/assets/supplier-management-overview.pptx",
    thumb: "/assets/thumbs/supplier.jpg",
  },
];

// Photography & Graphic Design, Virginia Beach event & brand work
export const CREATIVE = [
  {
    key: "future-of-sound",
    title: "The Future of Sound",
    tag: "Event Flyer · Timbaland Way Weekend",
    src: "/assets/meet-greet.jpg",
    span: "md:col-span-4 md:row-span-2",
  },
  {
    key: "timbaland-experience",
    title: "The Experience",
    tag: "Event Flyer · Timbaland x MOCA",
    src: "/assets/design-b.webp",
    span: "md:col-span-4 md:row-span-2",
  },
  {
    key: "tech-talent-expo",
    title: "Tech & Talent Expo",
    tag: "Event Flyer · Artist Submission",
    src: "/assets/design-c.webp",
    span: "md:col-span-4 md:row-span-2",
  },
  {
    key: "timbaland-quotes",
    title: "Best Timbaland Quotes",
    tag: "Social Editorial · Timbaland Way Cares",
    src: "/assets/design-a.webp",
    span: "md:col-span-4 md:row-span-2",
  },
  {
    key: "timbaland-way-card",
    title: "The Timbaland Way",
    tag: "Brand Identity · QR Card",
    src: "/assets/timbaland-way-card.png",
    span: "md:col-span-4",
  },
  {
    key: "earbuds",
    title: "Product Mock Up",
    tag: "Product Photography",
    src: "/assets/earbuds.jpg",
    span: "md:col-span-4",
  },
  {
    key: "headphone-case",
    title: "Product Mock Up",
    tag: "Product Concept Illustration",
    src: "/assets/headphone-case.jpg",
    span: "md:col-span-8",
  },
];

// Web design, fully hand-designed (no AI)
export const WEB_NOTE =
  "Every site here I designed and built entirely by hand, no AI involved. The portfolio you're reading right now is the one I built with AI tools, so you can see both sides of how I work.";

export const WEBSITES = [
  {
    key: "ericspence",
    title: "Eric Spence",
    tag: "Executive Profile Site",
    url: "https://www.timbalandwaycares.org/ericspence",
    img: "/assets/sites/ericspence.jpg",
  },
  {
    key: "cares",
    title: "Timbaland Way Cares",
    tag: "Nonprofit Foundation",
    url: "https://www.timbalandwaycares.org/",
    img: "/assets/sites/cares.jpg",
  },
  {
    key: "weekend",
    title: "Timbaland Way Weekend",
    tag: "Event Experience",
    url: "https://timbalandwayweekend.com/",
    img: "/assets/sites/weekend.jpg",
  },
  {
    key: "timbotheking",
    title: "Timbo Da King",
    tag: "Artist Site · Timbaland",
    url: "https://timbotheking.com/",
    img: "/assets/sites/timbotheking.jpg",
  },
];

// Videography, event films
export const PHOTOGRAPHY = [
  { key: "photo-01", tag: "Editorial Portrait", src: "/assets/photography/photo-01.jpg" },
  { key: "photo-02", tag: "Community Event", src: "/assets/photography/photo-02.jpg" },
  { key: "photo-03", tag: "Live Crowd", src: "/assets/photography/photo-03.jpg" },
  { key: "photo-04", tag: "Boys & Girls Clubs Ambassador", src: "/assets/photography/photo-04.jpg" },
  { key: "photo-05", tag: "Live Q&A", src: "/assets/photography/photo-05.jpg" },
  { key: "photo-06", tag: "NSU Signing Day", src: "/assets/photography/photo-06.jpg" },
  { key: "photo-07", tag: "Access College Foundation", src: "/assets/photography/photo-07.jpg" },
  { key: "photo-08", tag: "Arena Lights", src: "/assets/photography/photo-08.jpg" },
  { key: "photo-09", tag: "Studio Portrait", src: "/assets/photography/photo-09.jpg" },
];

export const VIDEOS = [
  {
    key: "event-film",
    title: "Event Film, B-Roll",
    tag: "Videography · Virginia Beach",
    src: "/assets/video-event.mp4",
    poster: "/assets/poster-event.jpg",
  },
  {
    key: "group-shot",
    title: "The Group Shot",
    tag: "Videography · Event Coverage",
    src: "/assets/video-groupshot.mp4",
    poster: "/assets/poster-groupshot.jpg",
  },
  {
    key: "interview",
    title: "Interview Feature",
    tag: "Videography · On-Camera",
    src: "/assets/video-damon.mp4",
    poster: "/assets/poster-damon.jpg",
  },
  {
    key: "slow-walk",
    title: "Slow Walk",
    tag: "Videography · Cinematic",
    src: "/assets/video-slowwalk.mp4",
    poster: "/assets/poster-slowwalk.jpg",
  },
  {
    key: "recap",
    title: "Event Recap",
    tag: "Videography · Highlight Reel",
    src: "/assets/video-recap.mp4",
    poster: "/assets/poster-recap.jpg",
  },
];

export const RATE_CARD = {
  low: "$45",
  high: "$85",
  unit: "/ hour",
  note: "Across my career, my full-time Instructional Design rates have ranged from $45 to $85 per hour, dependent on client needs, title, and travel requirements.",
};

export const CONTRACTOR_NOTE =
  "For recruiters: I work as a contractor. Clients bring me in to own a specific part of a larger project, so my engagements are scoped by design. Across my career they've run anywhere from four weeks to three years. Each one is a focused deliverable I was hired to complete, not a job I left.";

export const FLIPS = [
  { key: "silo-ridge", address: "6 Silo Ridge Rd", price: "$1,300,000", src: "/assets/flips/flip-silo-ridge.jpg" },
  { key: "singletree", address: "139 Singletree", price: "$1,040,000", src: "/assets/flips/flip-singletree.jpg" },
  { key: "sage-brush", address: "21334 Sage Brush Ln", price: "$970,000", src: "/assets/flips/flip-sage-brush.jpg" },
  { key: "central-park", address: "1335 Central Park Ave", price: "$635,000", src: "/assets/flips/flip-central-park.jpg" },
];

// Interior design
export const INTERIOR_INTRO =
  "I specialize in interior design and space planning, creating residential and commercial spaces that are functional, elevated, and visually cohesive. As a six-year Illinois licensed REALTOR\u00AE and Construction Project Manager with a multimillion-dollar real estate flip portfolio, I bring a unique understanding of design, renovation, construction, property value, and how to transform a space into something both beautiful and marketable.";

export const INTERIOR_CREDS = [
  "Interior Designer",
  "Illinois Licensed REALTOR\u00AE · 6 years",
  "Construction Project Manager",
  "Multimillion-dollar flip portfolio",
];

export const INTERIOR_LICENSE = "/assets/interior/realtor-license.jpg";
export const INTERIOR_COUNT = 25;

// Selected experience timeline
export const EXPERIENCE = [
  { period: "2026", company: "Southern Company", role: "ERP Trainer (Stand-In)", note: "Facilitated CSS to C2M implementation training after a Train-the-Trainer bootcamp." },
  { period: "2024 — 2026", company: "New Balance · UCOP", role: "Instructional Designer", note: "Built Rise and Storyline courses, AI-narrated video, and AI-tools training in a heavy Agile/Jira environment; managed Workday Learning uploads." },
  { period: "2022 — 2023", company: "Whole Foods · Atlantic Health", role: "Instructional Designer", note: "Oracle Cloud Finance and Supply Chain training; Train-the-Trainer and change management." },
  { period: "2021 — 2023", company: "Philadelphia Gas Works", role: "Instructional Designer / Trainer", note: "Oracle C2M implementation content: Captivate, Camtasia, weekly LMS uploads, SCORM compliance." },
  { period: "2019 — 2020", company: "WSSC Water", role: "Sr. ERP Trainer", note: "UPK content for WAM, C2M, CC&B and MWM; UPK admin, LMS management, field and corporate facilitation, hypercare." },
  { period: "2017", company: "Cook County Government", role: "Training Content Developer / ERP Trainer", note: "Oracle R12 and Hyperion UPK content; facilitated ILT for 3,500 users." },
  { period: "2013 — 2016", company: "Motorola · Meritor · Integra & more", role: "Lead Training Developer", note: "Led UPK development teams; multi-language content conversions; global Train-the-Trainer." },
  { period: "2005 — Present", company: "Bling! Magazine · Timbaland Way Cares", role: "Graphic Designer & Photographer (Part-Time)", note: "20 years of design work: magazine covers, event branding, product photography, and videography for events around Virginia Beach. The creative roots that still shape every course I build." },
];

export const CERTS = [
  { title: "AI Fundamentals, Google", file: "/assets/certs/google-ai-fundamentals.jpg" },
  { title: "Project Management with AI, Coursera", file: "/assets/certs/coursera-pm-ai.pdf" },
  { title: "Certified ScrumMaster (CSM)", file: "/assets/certs/csm.pdf" },
  { title: "Oracle UPK Certified Implementation Specialist", file: "/assets/certs/ecertificate.pdf" },
];

export const EDUCATION = [
  "Multimedia Production & Design, Miami International University of Art & Design (collegiate study; degree not completed)",
  "Human Behavioral Science, Ashford University (one year of study that shapes how I design for real learners)",
  "Commercial Arts, Chicago Vocational Career Academy (High School, graduated 2003)",
];

export const TESTIMONIALS = [
  {
    quote:
      "Jessica joined Global Retail Training at New Balance in a contract Instructional Design role and quickly proved herself as a valued member of the team. She is highly skilled at designing and developing engaging elearning courses and other training content to drive staff development and impact business objectives. She worked effectively both within our team as well as cross-functionally with our business partners. She stands out for her creativity, critical thinking, and solutions-oriented mindset. Jessica's contributions will undoubtedly be invaluable to any organization and project.",
    name: "Alisa Barton, M.Ed",
    title: "Training Manager & Instructional Designer (Retired)",
    context: "Managed Jessica directly \u00b7 New Balance",
  },
];
